import { Fragment, lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

// Sprint 32F — /r/<slug> permalink → /imagine/compose?r=<slug>.
// Inline because it's a one-line redirect.
function RecipeRedirect(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/imagine/compose?r=${encodeURIComponent(slug ?? '')}`} replace />;
}

// Sprint 32 polish — cross-domain SEO redirect for /w/recipe/<slug>.
// Recipes share the SAME slug + content across both domains
// (composition_recipes is one DB table). Hosting the wiki landing on
// BOTH www.jubit.ai and gghere.com would create duplicate-content SEO
// penalties. Strategy: jubuddy redirects /w/recipe/<slug> to the
// canonical surface on www.jubit.ai. Active-use surface (/r/<slug>)
// stays local on each domain so the composer experience is unbroken.
//
// Uses a permanent 308-equivalent client redirect (window.location).
// We can't use <Navigate/> across origins; React Router Navigate is
// same-origin only. window.location.replace gives a clean redirect
// without leaving a history entry to back-button into.
function CrossDomainWikiRedirect(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  useEffect(() => {
    const target = `https://www.jubit.ai/w/recipe/${encodeURIComponent(slug ?? '')}`;
    window.location.replace(target);
  }, [slug]);
  return (
    <div className="p-8 text-center text-sm text-[var(--text-secondary)]">
      Redirecting to canonical recipe page on www.jubit.ai…
    </div>
  );
}
// 2026-05-11 (ADR-0165) — Landing.tsx is preserved as a module (its
// named helpers may still be useful for follow-ups) but the default
// LandingPage route is unused; `/landing` now redirects to /home.
// We keep the import-as-side-effect-free behavior by NOT importing
// the unused symbol; tree-shaking will drop the LandingPage component
// from the build entirely.
// The original game routes. Lazy since 2026-09-12: with the battle UI and the
// simulator they were about a third of the main entry chunk, paid on first
// paint of every other surface — /home, /planet, /jub and the Jubit embed.
// A direct visit to one of them now costs one route-chunk fetch.
const FusionWorkshopPage = lazy(() =>
  import('./routes/FusionWorkshop.tsx').then((m) => ({ default: m.FusionWorkshopPage })),
);
const HeroesPage = lazy(() =>
  import('./routes/Heroes.tsx').then((m) => ({ default: m.HeroesPage })),
);
const CampaignPage = lazy(() =>
  import('./routes/Campaign.tsx').then((m) => ({ default: m.CampaignPage })),
);
const PlayPage = lazy(() =>
  import('./routes/Play.tsx').then((m) => ({ default: m.PlayPage })),
);
const GameHubPage = lazy(() =>
  import('./routes/GameHub.tsx').then((m) => ({ default: m.GameHubPage })),
);
// ADR-0317 — crayon desk + garden-toy index. Lazy so Open Doodle SVGs
// stay out of the /game hub chunk (bundle budget).
const DoodleDeskPage = lazy(() =>
  import('./routes/DoodleDesk.tsx').then((m) => ({ default: m.DoodleDeskPage })),
);
const GardenToysPage = lazy(() =>
  import('./routes/GardenToys.tsx').then((m) => ({ default: m.GardenToysPage })),
);
// Phase η — heavy creative + post-auth surfaces lazy-loaded so they don't
// pay the bundle cost on first paint of marketing (/home), game routes
// (/play, /campaign, /heroes, /fusion), or the embed surface. Each
// gets its own chunk; first visit is one cold-fetch then cached.
const SettingsPage = lazy(() =>
  import('./routes/Settings.tsx').then((m) => ({ default: m.SettingsPage })),
);
const SettingsBillingPage = lazy(() =>
  import('./routes/SettingsBilling.tsx').then((m) => ({ default: m.SettingsBillingPage })),
);
const HeroCardsPage = lazy(() =>
  import('./routes/HeroCards.tsx').then((m) => ({ default: m.HeroCardsPage })),
);
// The published animated-standee roster + asset-review surface. Lazy so the
// three.js preview cost lands only when someone opens it.
const StandeesPage = lazy(() =>
  import('./routes/Standees.tsx').then((m) => ({ default: m.StandeesPage })),
);
const CombatGirlsPage = lazy(() =>
  import('./routes/CombatGirls.tsx').then((m) => ({ default: m.CombatGirlsPage })),
);
const GgHereHomePage = lazy(() =>
  import('./worlds/GgHereHomePage.tsx').then((m) => ({ default: m.GgHereHomePage })),
);
const StudioPage = lazy(() =>
  import('./routes/Studio.tsx').then((m) => ({ default: m.StudioPage })),
);
const ImaginePage = lazy(() =>
  import('./routes/Imagine.tsx').then((m) => ({ default: m.ImaginePage })),
);
// Sprint 31.5 — prompt-factory mode for /imagine. Lazy-loaded so the
// existing free-form /imagine doesn't pay the @jubit-ai/prompt-composer
// bundle cost until the user explicitly opts in. Same pattern as JubApp + PictorialBookPage.
const ImagineComposePage = lazy(() =>
  import('./routes/ImagineCompose.tsx').then((m) => ({ default: m.ImagineComposePage })),
);
// ADR-0243 — the WebGPU fluid lab. Direct-URL only (no Landing link); the page
// itself lazy-loads the three-webgpu chunk, so this route chunk stays tiny.
const FluidLabPage = lazy(() =>
  import('./lab/FluidLabPage.tsx').then((m) => ({ default: m.FluidLabPage })),
);
// The ability-VFX lab. Direct-URL only, same shape as the fluid lab above: the
// page lazy-loads the stage (and three) itself, so this route chunk stays tiny.
const VfxLabPage = lazy(() =>
  import('./lab/VfxLabPage.tsx').then((m) => ({ default: m.VfxLabPage })),
);
// A local, unsaved room blockout study. Both the route and import disappear in production.
const RoomLabPage = import.meta.env.DEV
  ? lazy(() => import('./lab/RoomLabPage.tsx').then((m) => ({ default: m.RoomLabPage })))
  : null;
// ADR-0321 — the Ether Sea between the city planets. Same two-hop shape as the
// labs: the page lazy-loads the stage (and three) itself, so this chunk stays tiny.
const SeaPage = lazy(() => import('./sea/SeaPage.tsx').then((m) => ({ default: m.SeaPage })));
// Inner-sea roguelike: Ultra Siege juice + Ether Sea helm, under the city blocks.
const VoyagePage = lazy(() => import('./voyage/VoyagePage.tsx').then((m) => ({ default: m.VoyagePage })));
const ProfilePage = lazy(() =>
  import('./routes/Profile.tsx').then((m) => ({ default: m.ProfilePage })),
);
const ChatlabPage = lazy(() =>
  import('./routes/Chatlab.tsx').then((m) => ({ default: m.ChatlabPage })),
);
import { EmbedCompanionPage } from './routes/EmbedCompanion.tsx';
import { REGIONS, type RegionId } from './planet/data/districts.ts';
import { NotFoundPage } from './routes/NotFound.tsx';
import { HealthPage } from './routes/Health.tsx';
import { AuthCallbackPage } from './routes/AuthCallback.tsx';
import { SsoCallbackPage } from './routes/SsoCallback.tsx';
import { ByopCallbackPage } from './routes/ByopCallback.tsx';
import { PrivacyPage } from './routes/Privacy.tsx';
import { TermsPage } from './routes/Terms.tsx';

// Sprint 28.6: admin queue is lazy-loaded so it stays out of the main
// bundle (~80 KB gz of admin tables + actions; only loaded by admins).
const AdminGraphPage = lazy(() =>
  import('./routes/AdminGraph.tsx').then((m) => ({ default: m.AdminGraphPage })),
);
// Max-Tier Gold Prompt (Phase 3) — gold-vocabulary curation. Lazy, admin-only.
const AdminPromptsPage = lazy(() =>
  import('./routes/AdminPrompts.tsx').then((m) => ({ default: m.AdminPromptsPage })),
);
// 2026-05-11 (Move D, Layer 3 spec) — Featured admin tooling. Same
// lazy-load shape as AdminGraph; only admins touch it.
const AdminFeaturedPage = lazy(() =>
  import('./routes/AdminFeatured.tsx').then((m) => ({ default: m.AdminFeaturedPage })),
);
// 2026-05-25 (Phase E, ADR-0184) — Library suggestions admin queue.
// Lazy so the queue table + decide buttons stay out of the main bundle.
const AdminLibrarySuggestionsPage = lazy(() =>
  import('./routes/AdminLibrarySuggestions.tsx').then((m) => ({ default: m.AdminLibrarySuggestionsPage })),
);
// ADR-0310: the planet's moderation queue. Same lazy shape — only admins load it.
const AdminPlanetCreationsPage = lazy(() =>
  import('./routes/AdminPlanetCreations.tsx').then((m) => ({ default: m.AdminPlanetCreationsPage })),
);
const AdminShopPage = lazy(() =>
  import('./routes/AdminShopPage.tsx').then((m) => ({ default: m.AdminShopPage })),
);
const AdminPostPage = lazy(() => import('./routes/AdminPost.tsx').then((m) => ({ default: m.AdminPostPage })));
const PlanetOrdersPage = lazy(() =>
  import('./routes/PlanetOrdersPage.tsx').then((m) => ({ default: m.PlanetOrdersPage })),
);
// Private advisory planets. The route chunk is the lock; the readings load
// inside the page only after Jubit confirms the owner. Not a walked city.
const PlanetPlanningPage = lazy(() =>
  import('./routes/PlanetPlanning.tsx').then((m) => ({ default: m.PlanetPlanningPage })),
);
// ADR-0044 (Jub Anime Studio): kept out of main chunk so /jub users pay
// the bundle cost only when they visit. Mobile-first surface; rest of
// app unchanged. Import JubApp.tsx directly (not the jub/index.ts
// barrel) so the chunk is named JubApp-*.js — a barrel-named chunk
// comes out as index-*.js and masquerades as the entry in bundle
// measurements.
// ADR-0296 — Jub Poker · 聚牌: the five play-chip card tables taken over from
// M1zwell/texas-holdem. One lazy chunk serves the lobby and every table; the
// engine is @jubuddy/poker-core and every table runs in the browser against bots.
const PokerPage = lazy(() =>
  import('./routes/Poker.tsx').then((m) => ({ default: m.PokerPage })),
);
const JubApp = lazy(() => import('./jub/JubApp.tsx').then((m) => ({ default: m.JubApp })));
// ADR-0079 (Sprint 6 M1 — Pictorial Book): lazy-loaded so the empty
// stub doesn't grow the main chunk. M4 fills the route with the actual
// Pokédex grid + detail screens.
const PictorialBookPage = lazy(() =>
  import('./routes/PictorialBook.tsx').then((m) => ({ default: m.PictorialBookPage })),
);
// ADR-0106 — `/i/<id>` viewer. Lazy so the ImageDetailViewer +
// HDLightbox + ResultActionBar bundle pays its ~6-8 KB gz only when a
// user actually navigates into image detail (typical anonymous landing
// is /jub or /pictorial-book; the viewer is one click away).
const ImageDetailPage = lazy(() =>
  import('./routes/ImageDetail.tsx').then((m) => ({ default: m.ImageDetailPage })),
);
// ADR-0159 (Sprint C) — `/apps` discoverability index. Lazy because it's
// a low-traffic surface (most users land on /jub directly); the static
// app grid pays ~1 KB gz only when explicitly navigated to.
const AppsPage = lazy(() =>
  import('./routes/Apps.tsx').then((m) => ({ default: m.AppsRoute })),
);
const CreatureStagesPage = lazy(() =>
  import('./routes/CreatureStages.tsx').then((m) => ({ default: m.CreatureStagesPage })),
);
// ADR-0192 — Pokémon-style turn-based creature battle (hero as active fighter).
// Lazy so the resolver + battle UI stay out of the main bundle.
const CreatureBattlePage = lazy(() =>
  import('./creatures/battle/CreatureBattle.tsx').then((m) => ({ default: m.CreatureBattlePage })),
);
// ADR-0217 — Bastion siege raids (CoC-style live-deploy battles). Own lazy chunk.
const SiegePage = lazy(() =>
  import('./routes/Siege.tsx').then((m) => ({ default: m.SiegePage })),
);
// ADR-0227 — Arcane TD (web-native, Tangy-inspired live TS-sim TD). Own lazy chunk.
const ArcanePage = lazy(() =>
  import('./routes/Arcane.tsx').then((m) => ({ default: m.ArcanePage })),
);
// ADR-0218 — the Bastion base editor + gold economy. Own lazy chunk.
const BasePage = lazy(() =>
  import('./routes/Base.tsx').then((m) => ({ default: m.BasePage })),
);
// ADR-0192 (Option 2) — Creature TCG: collect (packs) → build squad → battle.
const CreatureCollectionPage = lazy(() =>
  import('./routes/CreatureCollection.tsx').then((m) => ({ default: m.CreatureCollectionPage })),
);
// Strategy Dex — searchable original move/ability/type vocabulary (Codex slice).
const StrategyDexPage = lazy(() =>
  import('./routes/StrategyDex.tsx').then((m) => ({ default: m.StrategyDexPage })),
);
// Stage Layouts — the 32 campaign stages as themed "lego" boards (Codex asset kit).
const StagesPage = lazy(() => import('./routes/Stages.tsx').then((m) => ({ default: m.StagesPage })));
// Smogon-style sectioned dex — section landings + record detail pages.
const StrategyDexSectionPage = lazy(() =>
  import('./routes/StrategyDexSection.tsx').then((m) => ({ default: m.StrategyDexSectionPage })),
);
const StrategyDexEntryPage = lazy(() =>
  import('./routes/StrategyDexEntry.tsx').then((m) => ({ default: m.StrategyDexEntryPage })),
);
const TcgCardLabPage = lazy(() =>
  import('./routes/TcgCardLab.tsx').then((m) => ({ default: m.TcgCardLabPage })),
);
const MeshyRefShelfPage = lazy(() =>
  import('./routes/MeshyRefShelf.tsx').then((m) => ({ default: m.MeshyRefShelfPage })),
);
// 2026-05-11 (ADR-0165) — Brand.tsx is preserved as a module because
// Home.tsx consumes its `VARIANT_STORIES` + `SURFACE_ROWS` named
// exports. The default `BrandPage` is unused; `/brand` now redirects
// to `/home`. Tree-shaking drops the unused page component.
// 2026-05-11 (ADR-0165) — merged `/home` marketing page. Replaces the
// `/` → /jub redirect (anonymous users now land on marketing) and
// supersedes /landing + /brand (both now redirect here). Lazy so the
// in-product /jub bundle doesn't pay for marketing chrome.
const HomePage = lazy(() =>
  import('./routes/Home.tsx').then((m) => ({ default: m.HomePage })),
);
// ADR-0207 — explorable 3D "tile-planet" (HK small planet à la Messenger).
// Lazy so three.js (its own chunk) never loads on first paint.
const PlanetPage = lazy(() =>
  import('./planet/PlanetRoute.tsx').then((m) => ({ default: m.PlanetPage })),
);
import { RouteFallback } from './components/RouteFallback.tsx';
import { ChunkErrorBoundary } from './components/ChunkErrorBoundary.tsx';
// Dev never wraps import(), so nothing can tag a failed chunk load there and the boundary
// could only rethrow — muddling React's error attribution while it did. Production only.
const RouteChunkBoundary = import.meta.env.PROD ? ChunkErrorBoundary : Fragment;
import { useAudioInit } from './audio/useAudioInit.ts';
import { applyThemeToHtml, useThemeStore } from './state/themeStore.ts';
import { useBridgeDrainer } from './state/bridgeDrainer.ts';
import { subscribeToAuthChanges, useUserStore } from './state/userStore.ts';
// 2026-05-11 (ADR-0166) — multi-language Context wraps the entire
// route tree so /home (the v1 consumer) + any future route can call
// useI18n(). Resolution priority: URL ?lang → localStorage →
// navigator.language → 'en' fallback. Zero-cost on routes that
// never call useI18n (Context provider is a thin wrapper).
import { GlobalAuthChipMount } from './components/account/GlobalAuthChip.tsx';
import { RefillReturnToast } from './credits/RefillReturnToast.tsx';
import { I18nProvider } from './i18n/I18nProvider.tsx';
import { isPlanetDomainNow } from './worlds/planetDomain.ts';
import { useSeoHead } from './worlds/useSeoHead.ts';

export default function App() {
  useAudioInit();
  // 2026-08-02 — gghere.com is canonical for the planet worlds on BOTH domains.
  // Prerendered shells cover first paint; this keeps <head> right across
  // client-side navigation. No-op on every non-planet route.
  useSeoHead();
  // Sprint 11: sync dark/light class on <html> whenever the store changes.
  const theme = useThemeStore((s) => s.theme);
  useEffect(() => {
    applyThemeToHtml(theme);
  }, [theme]);
  // Sprint 15 (ADR-0008): drain the credit-bridge POST queue when linked + online.
  // No-op when VITE_BRIDGE_URL is unset.
  useBridgeDrainer();
  // ADR-0021: bootstrap session + profile, and react to sign-in/out events.
  const refresh = useUserStore((s) => s.refresh);
  useEffect(() => {
    void refresh();
    return subscribeToAuthChanges();
  }, [refresh]);
  // ADR-0177: game-state sync — hydrate progression/fusion/loadout from
  // the shared Jubit Supabase on sign-in; debounced upsert on local
  // change. Degrades silently when the table is missing or user is
  // anonymous, so this is safe to run unconditionally.
  useEffect(() => {
    void import('./lib/game-state/gameStateBoot').then((m) =>
      m.initGameStateBoot(),
    );
  }, []);
  return (
    // ADR-0047 — .jub-app lifted to app shell so every route inherits the
    // unified token aliases (--jub-* → var(--*)) and component utilities
    // (.jub-pill, .jub-card, .jub-tabbar, .jub-btn-primary). Routes that
    // need to force-pin dark colors locally (Imagine, Chatlab) wrap their
    // body in <div className="dark-scope">. JubApp keeps its own inner
    // .jub-app wrapper because it carries dynamic is-dark/is-light theme
    // tags; nested .jub-app is harmless (CSS rules merge).
    // ADR-0166 — <I18nProvider> from main wraps everything so route
    // components can call useT() for the 6-locale strings catalog.
    <I18nProvider>
    <div className="jub-app">
    {/* Floating Jubit identity affordance for the chrome-less surfaces
        (jubuddy.com/planet, gghere.com, gghere.com/planet). Those render a
        full-bleed canvas and mount none of the /jub shells, so they carried no
        way to sign in and no indication of who was signed in. Suppresses itself
        on /jub (which has its own AccountChip) and on the gameplay canvases. */}
    <GlobalAuthChipMount />
    {/* ADR-0331 / P7-050 — one refill-return observer for every route.
        Stripe returns to the originating URL (`?refill=success|cancelled`).
        Mounting only inside JubApp missed /profile, /planet, and /imagine. */}
    <RefillReturnToast />
    {/* A lazy chunk that fails to load (a tab left open across a deploy) gets a
        Reload prompt instead of unmounting the app to a white page. Any other
        error passes through untouched. */}
    <RouteChunkBoundary>
    <Routes>
      {/* 2026-05-11 (ADR-0165) — `/` and `/home` now serve the merged
          marketing homepage. This reverses the 2026-05-09 directive
          that pointed `/` at `/jub`; anonymous users land on marketing
          first, with a prominent "Open the app →" CTA in the hero to
          one-click into /jub. /landing and /brand redirect here. */}
      {/* 2026-08-02 — gghere.com is the planet-city-game domain and gets its own
          front door; jubuddy.com keeps the marketing home. Both domains serve the
          same bundle from one Vercel project, so the HTML SHELL is chosen at the
          edge (vercel.json host rewrite) and the COMPONENT is chosen here. Both
          layers are needed: the rewrite fixes the social card for scrapers that
          never run JS, this fixes what actually renders. See
          docs/superpowers/specs/2026-08-02-gghere-planet-domain-consolidation-design.md */}
      <Route
        path="/"
        element={
          <Suspense fallback={<RouteFallback />}>
            {isPlanetDomainNow() ? <GgHereHomePage /> : <HomePage />}
          </Suspense>
        }
      />
      {/* The hub also has its own stable URL, so it is reachable from jubuddy.com
          and from preview deploys where no gghere hostname exists. */}
      <Route
        path="/worlds"
        element={
          <Suspense fallback={<RouteFallback label="Worlds" />}>
            <GgHereHomePage />
          </Suspense>
        }
      />
      <Route
        path="/home"
        element={
          <Suspense fallback={<RouteFallback />}>
            <HomePage />
          </Suspense>
        }
      />
      {/* 2026-05-11 (ADR-0165) — /landing redirects to /home.
          The LandingPage component is still imported above so it stays
          part of the build until a follow-up PR deletes it; this route
          just rewrites the URL. */}
      <Route path="/landing" element={<Navigate to="/home" replace />} />
      <Route
        path="/fusion"
        element={
          <Suspense fallback={<RouteFallback label="Fusion Workshop" />}>
            <FusionWorkshopPage />
          </Suspense>
        }
      />
      <Route
        path="/heroes"
        element={
          <Suspense fallback={<RouteFallback label="Heroes" />}>
            <HeroesPage />
          </Suspense>
        }
      />
      <Route
        path="/hero-cards"
        element={
          <Suspense fallback={<RouteFallback label="Hero Cards" />}>
            <HeroCardsPage />
          </Suspense>
        }
      />
      <Route
        path="/game"
        element={
          <Suspense fallback={<RouteFallback label="Game hub" />}>
            <GameHubPage />
          </Suspense>
        }
      />
      <Route
        path="/game/doodle"
        element={
          <Suspense fallback={<RouteFallback label="Doodle desk" />}>
            <DoodleDeskPage />
          </Suspense>
        }
      />
      <Route
        path="/game/toys"
        element={
          <Suspense fallback={<RouteFallback label="Garden toys" />}>
            <GardenToysPage />
          </Suspense>
        }
      />
      <Route
        path="/poker"
        element={
          <Suspense fallback={<RouteFallback label="Jub Poker" />}>
            <PokerPage />
          </Suspense>
        }
      />
      {/* ADR-0297 — live tables on the Fly.io server + the look-before-you-sit
          preview. Declared before /poker/:game so they never fall into the solo switch. */}
      <Route
        path="/poker/table/:id"
        element={
          <Suspense fallback={<RouteFallback label="Jub Poker" />}>
            <PokerPage />
          </Suspense>
        }
      />
      <Route
        path="/poker/join"
        element={
          <Suspense fallback={<RouteFallback label="Jub Poker" />}>
            <PokerPage />
          </Suspense>
        }
      />
      <Route
        path="/poker/:game"
        element={
          <Suspense fallback={<RouteFallback label="Jub Poker" />}>
            <PokerPage />
          </Suspense>
        }
      />
      <Route
        path="/planet"
        element={
          <Suspense fallback={<RouteFallback label="Planet" />}>
            <PlanetPage />
          </Suspense>
        }
      />
      <Route
        path="/planet/orders"
        element={
          <Suspense fallback={<RouteFallback label="My printed globes" />}>
            <PlanetOrdersPage />
          </Suspense>
        }
      />
      <Route
        path="/planet/planning"
        element={
          <Suspense fallback={<RouteFallback label="Private planet" />}>
            <PlanetPlanningPage />
          </Suspense>
        }
      />
      {/* Hong Kong also answers to /hk — the city-named alias every other world has.
          /planet stays canonical: it is the published WorldIndexV1 deep-link target
          (ADR-0228), so it can never move. basePath keeps a session on whichever
          alias it started from. */}
      <Route
        path="/hk"
        element={
          <Suspense fallback={<RouteFallback label="Hong Kong" />}>
            <PlanetPage basePath="/hk" />
          </Suspense>
        }
      />
      {/* Computed GAME worlds (ADR-0272) — same engine, no map data behind them.
          Not generated from REGIONS: `games` is not a RegionId, deliberately (a region
          must be a city with ten cube-belts; see GAMES_WORLD). */}
      <Route
        path="/games"
        element={
          <Suspense fallback={<RouteFallback label="Game worlds" />}>
            <PlanetPage world="games" />
          </Suspense>
        }
      />
      {/* Global-city planets — same engine, city-scoped picker + landing (ADR-0236).
          Generated from REGIONS so adding a city is a one-line registry edit. */}
      {(Object.keys(REGIONS) as RegionId[]).map((id) => (
        <Route
          key={id}
          path={`/${id}`}
          element={
            <Suspense fallback={<RouteFallback label={REGIONS[id].place} />}>
              <PlanetPage region={id} />
            </Suspense>
          }
        />
      ))}
      <Route
        path="/campaign"
        element={
          <Suspense fallback={<RouteFallback label="Campaign" />}>
            <CampaignPage />
          </Suspense>
        }
      />
      <Route
        path="/play"
        element={
          <Suspense fallback={<RouteFallback label="Battle" />}>
            <PlayPage />
          </Suspense>
        }
      />
      <Route
        path="/settings/billing"
        element={
          <Suspense fallback={<RouteFallback label="Billing" />}>
            <SettingsBillingPage />
          </Suspense>
        }
      />
      <Route
        path="/settings"
        element={
          <Suspense fallback={<RouteFallback label="Settings" />}>
            <SettingsPage />
          </Suspense>
        }
      />
      <Route
        path="/profile"
        element={
          <Suspense fallback={<RouteFallback label="Profile" />}>
            <ProfilePage />
          </Suspense>
        }
      />
      <Route
        path="/studio"
        element={
          <Suspense fallback={<RouteFallback label="Studio" />}>
            <StudioPage />
          </Suspense>
        }
      />
      <Route
        path="/imagine"
        element={
          <Suspense fallback={<RouteFallback label="Imagine" />}>
            <ImaginePage />
          </Suspense>
        }
      />
      {/* ADR-0159 (Sprint C) — discoverability index of jubuddy's
          creative and game surfaces. Lazy-loaded; SEO-
          friendly (no auth, indexable). */}
      <Route
        path="/apps"
        element={
          <Suspense fallback={<RouteFallback label="Apps" />}>
            <AppsPage />
          </Suspense>
        }
      />
      <Route
        path="/creature-stages"
        element={
          <Suspense fallback={<RouteFallback label="Creature Stages" />}>
            <CreatureStagesPage />
          </Suspense>
        }
      />
      <Route
        path="/siege"
        element={
          <Suspense fallback={<RouteFallback label="Bastion Raid" />}>
            <SiegePage />
          </Suspense>
        }
      />
      <Route
        path="/arcane"
        element={
          <Suspense fallback={<RouteFallback label="Arcane TD" />}>
            <ArcanePage />
          </Suspense>
        }
      />
      <Route
        path="/base"
        element={
          <Suspense fallback={<RouteFallback label="My Bastion" />}>
            <BasePage />
          </Suspense>
        }
      />
      <Route
        path="/creature-battle"
        element={
          <Suspense fallback={<RouteFallback label="Creature Battle" />}>
            <CreatureBattlePage />
          </Suspense>
        }
      />
      <Route
        path="/creature-collection"
        element={
          <Suspense fallback={<RouteFallback label="Creature Collection" />}>
            <CreatureCollectionPage />
          </Suspense>
        }
      />
      <Route
        path="/strategy-dex"
        element={
          <Suspense fallback={<RouteFallback label="Strategy Dex" />}>
            <StrategyDexPage />
          </Suspense>
        }
      />
      <Route
        path="/stages"
        element={
          <Suspense fallback={<RouteFallback label="Stage Layouts" />}>
            <StagesPage />
          </Suspense>
        }
      />
      <Route
        path="/strategy-dex/:section"
        element={
          <Suspense fallback={<RouteFallback label="Strategy Dex" />}>
            <StrategyDexSectionPage />
          </Suspense>
        }
      />
      <Route
        path="/strategy-dex/:section/:id"
        element={
          <Suspense fallback={<RouteFallback label="Strategy Dex" />}>
            <StrategyDexEntryPage />
          </Suspense>
        }
      />
      <Route
        path="/tcg-card-lab"
        element={
          <Suspense fallback={<RouteFallback label="TCG Card Lab" />}>
            <TcgCardLabPage />
          </Suspense>
        }
      />
      <Route
        path="/meshy-ref-shelf"
        element={
          <Suspense fallback={<RouteFallback label="Meshy Refs" />}>
            <MeshyRefShelfPage />
          </Suspense>
        }
      />
      {/* 2026-05-11 (ADR-0165) — /brand redirects to /home. The Brand
          showcase content (39-variant grid, surface matrix, brand
          discipline) is absorbed into /home; the named exports
          (VARIANT_STORIES + SURFACE_ROWS) from Brand.tsx are consumed
          by Home.tsx so the file stays part of the build. */}
      <Route path="/brand" element={<Navigate to="/home" replace />} />
      <Route
        path="/imagine/compose"
        element={
          <Suspense fallback={<RouteFallback label="Compose" />}>
            <ImagineComposePage />
          </Suspense>
        }
      />
      {/* Sprint 32F — recipe permalinks. /r/<slug> bounces into the
          composer with the recipe seeded via ?r=<slug>. Pairs with the
          /w/recipe/<slug> wiki node from PR #153. */}
      <Route path="/r/:slug" element={<RecipeRedirect />} />
      {/* ADR-0106 — image detail viewer. Mirrors jubit.ai /i/<id>. */}
      <Route
        path="/i/:id"
        element={
          <Suspense fallback={<RouteFallback label="image" />}>
            <ImageDetailPage />
          </Suspense>
        }
      />
      {/* Sprint 32 polish — cross-domain SEO redirect. Canonical wiki
          landing lives on www.jubit.ai; this redirect ensures gghere.com
          /w/recipe/<slug> hits the same canonical page rather than
          duplicating content per domain. */}
      <Route path="/w/recipe/:slug" element={<CrossDomainWikiRedirect />} />
      <Route
        path="/chatlab"
        element={
          <Suspense fallback={<RouteFallback label="Chatlab" />}>
            <ChatlabPage />
          </Suspense>
        }
      />
      <Route
        path="/lab/fluid"
        element={
          <Suspense fallback={<RouteFallback label="Fluid Lab" />}>
            <FluidLabPage />
          </Suspense>
        }
      />
      <Route
        path="/lab/vfx"
        element={
          <Suspense fallback={<RouteFallback label="VFX Lab" />}>
            <VfxLabPage />
          </Suspense>
        }
      />
      {import.meta.env.DEV && RoomLabPage && (
        <Route path="/lab/room" element={
          <Suspense fallback={<RouteFallback label="Room study" />}>
            <RoomLabPage />
          </Suspense>
        } />
      )}
      <Route
        path="/sea"
        element={
          <Suspense fallback={<RouteFallback label="Ether Sea" />}>
            <SeaPage />
          </Suspense>
        }
      />
      <Route
        path="/voyage"
        element={
          <Suspense fallback={<RouteFallback label="大航海肉鸽海战" />}>
            <VoyagePage />
          </Suspense>
        }
      />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      {/* Jubit's product registry advertises this path for jubuddy + gghere and builds
          every create_token redirect_url from it. Without the route the redirect 404s
          and the one-time token is spent for nothing. */}
      <Route path="/auth/sso-callback" element={<SsoCallbackPage />} />
      <Route path="/byop/callback" element={<ByopCallbackPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route
        path="/admin/graph"
        element={
          <Suspense fallback={<RouteFallback label="Admin Graph" />}>
            <AdminGraphPage />
          </Suspense>
        }
      />
      <Route
        path="/admin/prompts"
        element={
          <Suspense fallback={<RouteFallback label="Admin Prompts" />}>
            <AdminPromptsPage />
          </Suspense>
        }
      />
      <Route
        path="/admin/featured"
        element={
          <Suspense fallback={<RouteFallback label="Admin Featured" />}>
            <AdminFeaturedPage />
          </Suspense>
        }
      />
      <Route
        path="/admin/library-suggestions"
        element={
          <Suspense fallback={<RouteFallback label="Library Suggestions" />}>
            <AdminLibrarySuggestionsPage />
          </Suspense>
        }
      />
      <Route
        path="/admin/planet-creations"
        element={
          <Suspense fallback={<RouteFallback label="Planet Creations" />}>
            <AdminPlanetCreationsPage />
          </Suspense>
        }
      />
      <Route
        path="/admin/shop"
        element={
          <Suspense fallback={<RouteFallback label="Planet shop" />}>
            <AdminShopPage />
          </Suspense>
        }
      />
      <Route
        path="/admin/post"
        element={
          <Suspense fallback={<RouteFallback label="Post office" />}>
            <AdminPostPage />
          </Suspense>
        }
      />
      <Route path="/embed/companion" element={<EmbedCompanionPage />} />
      <Route path="/embed/health" element={<HealthPage />} />
      <Route
        path="/jub/*"
        element={
          <Suspense fallback={<RouteFallback label="Jub Studio" />}>
            <JubApp />
          </Suspense>
        }
      />
      <Route
        path="/pictorial-book/*"
        element={
          <Suspense fallback={<RouteFallback label="Pictorial Book" />}>
            <PictorialBookPage />
          </Suspense>
        }
      />
      <Route
        path="/combat-girls"
        element={
          <Suspense fallback={<RouteFallback label="Combat Girls" />}>
            <CombatGirlsPage />
          </Suspense>
        }
      />
      <Route
        path="/standees"
        element={
          <Suspense fallback={<RouteFallback label="Standees" />}>
            <StandeesPage />
          </Suspense>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </RouteChunkBoundary>
    </div>
    </I18nProvider>
  );
}
